import React from 'react';
import { Select, MenuItem, Button, makeStyles } from '@material-ui/core';
import ReplayIcon from '@material-ui/icons/Replay';
import { COLORS, DEBUG_LEVELS } from './logsConstants';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import InfoIcon from '@material-ui/icons/Info';
import AppTooltip from '../../atoms/Tooltip/AppTooltip';

const useStyles = makeStyles(() => ({
  configPanel: ({isWarning}) => ({
    borderBottom: `1px solid ${COLORS.border}`,
    backgroundColor: '#fafbfc',
    padding: isWarning ? '0' : '0 16px',
    height: 40,
    display: 'flex',
    alignItems: 'center',
    gap: 14,
  }),
  title: {
    fontSize: 12,
    fontWeight: 700,
    color: COLORS.textPrimary,
    whiteSpace: 'nowrap',
  },
  divider: {
    width: 1,
    height: 16,
    backgroundColor: COLORS.border,
    flexShrink: 0,
  },
  fields: {
    display: 'flex',
    alignItems: 'center',
    gap: 14,
    flexWrap: 'nowrap',
    overflow: 'visble',
  },
  field: {
    display: 'flex',
    alignItems: 'baseline',
    gap: 5,
    whiteSpace: 'nowrap',
  },
  fieldLabel: {
    fontSize: 11,
    fontWeight: 500,
    color: COLORS.textMuted,
    display: 'flex',
    gap: '3px'
  },
  fieldValue: {
    fontSize: 12,
    fontWeight: 600,
    color: COLORS.textPrimary,
    fontFamily: 'ui-monospace, "SF Mono", Menlo, Consolas, monospace',
  },
  select: {
    fontSize: '12px',
    border: `1px solid ${COLORS.borderStrong}`,
    borderRadius: 5,
    backgroundColor: '#fff',
    display: 'inline-flex',
    alignItems: 'center',
  },
  selectInput: {
    padding: '2px 24px 2px 8px',
    fontSize: '12px',
    color: COLORS.textPrimary,
    '&:focus': { backgroundColor: 'transparent' },
  },
  restartBtn: {
    textTransform: 'none',
    fontSize: '11.5px',
    fontWeight: 600,
    marginLeft: 'auto',
    padding: '2px 10px',
    minHeight: 0,
    lineHeight: 1.6,
    // Explicit colors here (not just relying on MUI's theme default) so this button can't
    // silently pick up whatever palette.primary the host app's theme happens to define.
    color: `${COLORS.accent} !important`,
    borderColor: `${COLORS.borderStrong} !important`,
    backgroundColor: '#ffffff !important',
    '&:hover': {
      borderColor: `${COLORS.accent} !important`,
      backgroundColor: `${COLORS.accentBg} !important`,
    },
  },
  notFound: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    gap: 8,
    padding: '7px 12px',
    backgroundColor: '#FFF8E1',
    border: '1px solid #FFE082',
  },
  
  notFoundIcon: {
    fontSize: 18,
    color: '#F9A825',
    flexShrink: 0,
  },
  
  notFoundMessage: {
    fontSize: 12,
    lineHeight: 1.4,
    color: '#7A5A00',
  },

  menuPaper: {
    maxHeight: 320,
  },
  menuItem: {
    padding: '6px 10px',
    minHeight: 'auto',
    '&:not(:last-child)': {
      borderBottom: '1px solid #f0f0f0',
    },
  },
  menuItemContent: {
    display: 'flex',
    flexDirection: 'column',
    lineHeight: 1.3,
  },
  menuItemTitle: {
    fontSize: '12.5px',
    fontWeight: 500,
    color: '#212121',
  },
  menuItemDesc: {
    fontSize: '11px',
    color: '#757575',
    marginTop: 1,
  },
}));

export default function RuntimeConfigPanel({
  debugLevel,
  onDebugLevelChange,
  sliceInfo,
  restarting,
  onRestartSlice,
}) {
  const classes = useStyles({isWarning: !sliceInfo?.sslice});
  return (
    <>
    {
      !sliceInfo?.sslice ? 
      <div className={classes.configPanel}>
        <div className={classes.notFound}>
          <ErrorOutlineIcon className={classes.notFoundIcon} />
          <span className={classes.notFoundMessage}>
            Last deployed configuration or configured service slice is unavailable for this blueprint.
          </span>
        </div>
      </div>
      :
      <div className={classes.configPanel}>
        <span className={classes.title}>Service Slice details</span>
        <div className={classes.divider} />
        <div className={classes.fields}>
          <div className={classes.field}>
            <span className={classes.fieldLabel}>Name</span>
            <span className={classes.fieldValue}>{sliceInfo.sslice}</span>
          </div>
          <div className={classes.field}>
            <span className={classes.fieldLabel}>Version</span>
            <span className={classes.fieldValue}>{sliceInfo.version}</span>
          </div>
          <div className={classes.field}>
            <span className={classes.fieldLabel}>Resource Slice</span>
            <span className={classes.fieldValue}>{sliceInfo.rslice}</span>
          </div>
          <div className={classes.field}>
            <span className={classes.fieldLabel}>
              Log level
              <AppTooltip title="Changing the log level will effect in the service slice">
                <InfoIcon style={{fontSize: '13px', cursor: 'pointer', fill: '#3b82f6'}}/>
              </AppTooltip>
            </span>
            <Select
              value={debugLevel}
              onChange={onDebugLevelChange}
              className={classes.select}
              classes={{ select: classes.selectInput }}
              id="log-level-filter"
              disableUnderline
              MenuProps={{
                classes: { paper: classes.menuPaper },
              }}
              renderValue={(selected) => {
                const selectedLevel = DEBUG_LEVELS.find((lvl) => lvl.value === selected);
                return selectedLevel ? selectedLevel.title : '';
              }}
            >
              {DEBUG_LEVELS.map((lvl) => (
                <MenuItem key={lvl.value} value={lvl.value} className={classes.menuItem}>
                  <div className={classes.menuItemContent}>
                    <span className={classes.menuItemTitle}>{lvl.title}</span>
                    <span className={classes.menuItemDesc}>{lvl.description}</span>
                  </div>
                </MenuItem>
              ))}
            </Select>
            </div>
        </div>
        <Button
          variant="outlined"
          size="small"
          className={classes.restartBtn}
          startIcon={<ReplayIcon style={{ fontSize: 14 }} />}
          onClick={onRestartSlice}
          disabled={restarting}
        >
          {restarting ? 'Restarting…' : 'Restart'}
        </Button>
      </div>
    }
    </>
  );
}